<script setup lang="ts" generic="T extends Data = Data">
import { cloneDeep } from 'lodash-es';
import Paginator, { PageState } from 'primevue/paginator';
import { ObjectUtils } from 'primevue/utils';
import Simplebar from 'simplebar-vue';
import {
  computed,
  inject,
  nextTick,
  onMounted,
  onUnmounted,
  Ref,
  ref,
  shallowRef,
  watch,
} from 'vue';

import Checkbox from 'lib/components/checkbox/Checkbox.vue';
import eventBus, { Events, TableEvent } from 'lib/event-bus';
import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import { default as useToast, UseToastConfig } from 'lib/utils/baseToast.util';
import { formatDate } from 'lib/utils/date.util';
import {
  exportToExcel,
  normalizeTableData,
} from 'lib/utils/exportToExcel.util';
import { default as mergePropsWithDefaults } from 'lib/utils/mergePropsWithDefaults.util';
import {
  getNestedPropertyValue,
  setNestedPropertyValue,
} from 'lib/utils/object.util';

import {
  ColumnTogglePreset,
  CombinedHeaders,
  Data,
  DataTableEmits,
  DataTableProps,
  DataTableRowReorderEvent,
  DataTableSlots,
  FetchListResponse,
  QueryParams,
  TableParentHeader,
  TogglePresetConfirmDialogProps,
  TreeTableColumns,
  TreeTableProps,
} from './DataTable.vue.d';
import HeaderCell from './HeaderCell.vue';
import { adjustMenuPositionHelper, isArrayIncluded } from './helpers';
import MultiRow from './MultiRow.vue';
import Animation from '../animation/Animation.vue';
import Button from '../button/Button.vue';
import CustomColumn from '../customcolumn/CustomColumn.vue';
import CustomColumnInstance from '../customcolumn/CustomColumn.vue.d';
import DialogConfirm from '../dialogconfirm/DialogConfirm.vue';
import { FilterMatchMode } from '../filtercontainer/FilterContainer.vue.d';
import Icon from '../icon/Icon.vue';
import useLoadingStore from '../loading/store';
import Menu from '../menu/Menu.vue';
import MenuInstance from '../menu/Menu.vue.d';
import Toast from '../toast/Toast.vue';
import ToggleSwitch from '../toggleswitch/ToggleSwitch.vue';

import 'simplebar-vue/dist/simplebar.min.css';

type TableRow = T & {
  /**
   * Define wether the data is a child
   */
  childRow?: boolean;
  /**
   * Internal
   */
  loadingRow?: boolean;
  noDataRow?: boolean;
  childRowHeader?: boolean;
  header?: string;
};

type DragableRow = TableRow & { draggable?: boolean; order?: number };

const props = withDefaults(
  defineProps<DataTableProps<T> | TreeTableProps<T>>(),
  {
    tableName: 'datatable',
    tableTitle: undefined,
    lazy: true,
    options: () => [],
    dataKey: '_id',
    disableKey: 'isDefault',
    selectionType: 'checkbox',
    customColumn: true,
    useOption: true,
    rowHeight: 'fixed',
  },
);

const emit = defineEmits<DataTableEmits<T>>();
const singleSelection = defineModel<T>('singleSelection');
defineSlots<DataTableSlots<T>>();

const { defaultProps, locale } = useWangsVueConfig('DataTable');
const config = computed(() => mergePropsWithDefaults(props, defaultProps));

const Preset = inject<Record<string, any>>('preset', {}).datatable;

const rowsPerPageOptions = [10, 25, 50, 100];
const useToastConfig = inject<UseToastConfig>('useToastConfig');
const toast = useToast(useToastConfig);

const { setLoading } = useLoadingStore();

const dataTableID = ((): string => {
  const path = window.location.pathname.replace('/', '').split('/').join('-');
  return `${path}-${props.tableName}`;
})();

const currentPageTableData = ref<TableRow[]>(props.data ?? []) as Ref<
  TableRow[]
>;
const originalTableData = shallowRef<T[]>(props.data ?? []);

const expandedRows = ref<Record<string, number>>({});
const visibleColumns = ref<TreeTableColumns<T>[]>(props.columns);
const tableElement = ref<HTMLTableElement>();

const checkboxSelection = shallowRef<T[]>([]);
const rowReorderEventPayload = shallowRef<DataTableRowReorderEvent<T>>();
const customColumnKey = shallowRef<number>(0);
const loadingTable = shallowRef<boolean>(false);
const loadingRows = shallowRef<boolean>(false);
const columnKey = shallowRef<number>(0);
const tableKey = shallowRef<number>(0);
const paginatorKey = shallowRef<number>(0);
const dragging = shallowRef<boolean>(false);
const draggedRow = shallowRef<DragableRow>();

// Components Instance REFs
const customColumn = ref<CustomColumnInstance>();
const optionMenu = ref<MenuInstance>();

/**
 * Clone of default row to be used in reorder and toggle visibility without mutating the default rows.
 * This `rowReorderData` ref will store the reordered table rows.
 *
 * On component loaded, we need to load the config to re-order the row list.
 */
const rowReorderData = ref<DragableRow[]>([]) as Ref<DragableRow[]>;

// Stores filter from event bus FilterContainer - Handles both static and dynamic table filters
const filterQueryParams = shallowRef<QueryParams>({});
const searchQueryParam = shallowRef<string>();
const tagQueryParam = shallowRef<string>();
const sortBy = shallowRef<string>();
const sortOrder = shallowRef<-1 | 1>();
const tablePage = shallowRef(1);
const tableRows = shallowRef(10);
const totalRecords = shallowRef(0);
const showConfirmToggle = ref<Record<string, boolean>>({});

const hasChildHeaders = computed<boolean>(
  () => !!combinedHeaders.value.child.length,
);

// If there are child headers, all of the other headers will have a rowspan of 2
const defaultHeaderRowSpan = computed<number>(() =>
  hasChildHeaders.value ? 2 : 1,
);

const combinedHeaders = computed(() => {
  const combined: CombinedHeaders<T> = {
    parent: [],
    child: [],
  };

  for (const column of visibleColumns.value) {
    if (column.parentHeader) {
      // If the column has a parent header, push it to the child array
      combined.child.push(column);

      /*
       * Determine if the column's parent header already
       * exists in the parent array
       */
      const matchParentColumn = combined.parent.find(
        (parent) => parent.field === column.parentHeader.field,
      );

      if (!matchParentColumn) {
        combined.parent.push({
          ...column.parentHeader,
          colspan: 1,
          rowspan: 1,
        });
      } else if (matchParentColumn.colspan) {
        // If parent header already exists, extend its colspan
        matchParentColumn.colspan++;
      }
    } else {
      /*
       * If the column doesn't have a parent, push it to the parents array.
       * Its rowspan will follow defaultHeaderRowSpan.
       */
      combined.parent.push(column);
    }
  }

  return combined;
});

const visibleChildTableColumns = computed(() => {
  if (props.childTableProps?.columns)
    return props.childTableProps?.columns.filter((col) => {
      return (
        !col.parentColumnsFields ||
        col.parentColumnsFields?.some((field) =>
          visibleColumns.value.some((c) => c.field === field),
        )
      );
    });

  return visibleColumns.value;
});

const columnsCount = computed(() => {
  return (
    visibleColumns.value.length +
    (props.selectionType === 'checkbox' ? 1 : 0) +
    (props.useOption ? 1 : 0) +
    (props.reorderable ? 1 : 0) +
    (props.treeTable ? 1 : 0)
  );
});

const queryParams = computed<QueryParams>(() => ({
  ...props.defaultQueryParams,
  search: props.lazy ? searchQueryParam.value : undefined,
  tag: props.lazy ? tagQueryParam.value : undefined,
  page: props.usePaginator ? tablePage.value : undefined,
  limit: props.usePaginator ? tableRows.value : undefined,
  sortOrder: sortOrder.value,
  sortBy: sortBy.value,
  ...(props.lazy ? filterQueryParams.value : {}), // This will reset the previous filters.
}));

const isExpandedAll = computed(() => {
  const rowsHasChildren = currentPageTableData.value?.filter(
    (d) => d.children?.length || d.hasChildren,
  );

  return (
    rowsHasChildren?.length &&
    rowsHasChildren.every((data) => expandedRows.value[data[props.dataKey]] > 0)
  );
});

/**
 * This computed variable determines whether the selection checkbox on header should be checked or not.
 * When select all data from bulk action, the tableData value should not be updated.
 * The 'isSelectedAll' will be true if either the all current page data is selected or all records selected.
 */
const isSelectedAll = computed(() => {
  if (
    props.disableAllRows ||
    !filterParentRowData()?.length ||
    props.selectionType !== 'checkbox' ||
    currentPageDisabledRows.value?.length === currentPageTableData.value.length
  )
    return false;

  const length = checkboxSelection.value?.length;

  const currentPageDataKey =
    filterDisabledRows(filterParentRowData())?.map((dt) => dt[props.dataKey]) ??
    [];

  const selectedDataKey =
    checkboxSelection.value?.map((dt) => dt[props.dataKey]) ?? [];

  const isCurrentPageAllSelected = selectedDataKey.length
    ? isArrayIncluded(currentPageDataKey, selectedDataKey)
    : false;

  const isAllRecordSelected =
    length > 0 &&
    length ===
      (totalRecords.value ?? 0) - (currentPageDisabledRows.value.length ?? 0);

  return isCurrentPageAllSelected || isAllRecordSelected;
});

const currentPageDisabledRows = computed(() =>
  getDisabledRows(props.data ?? filterParentRowData() ?? []),
);

const tableIsLoading = computed<boolean>(
  () => loadingTable.value || props.loading,
);

const showCustomColumn = computed<boolean>(
  () =>
    props.useOption || props.childTableProps?.useOption || props.customColumn,
);

const currentPageHasNoData = computed<boolean>(
  () => !tableIsLoading.value && !currentPageTableData.value?.length,
);

const renderedTableData = computed(() =>
  (props.lazy && props.fetchFunction) || !props.usePaginator
    ? currentPageTableData.value
    : currentPageTableData.value.slice(
        (tablePage.value - 1) * tableRows.value,
        tablePage.value * tableRows.value,
      ),
);

const isRowExpanded = (key: string): boolean => {
  const keys = Object.keys(expandedRows.value);
  return keys.includes(key);
};

const isRowDisabled = (key: string): boolean => {
  const keys = currentPageDisabledRows.value;
  return keys.includes(key);
};

const isRowSelected = (key: string): boolean => {
  return props.selectionType === 'checkbox'
    ? !!checkboxSelection.value.find((data) => data[props.dataKey] === key)
    : isSingleSelected(key);
};

const isSingleSelected = (key: string): boolean => {
  return key !== undefined && singleSelection.value?.[props.dataKey] === key;
};

const filterParentRowData = (rowData?: T[]): T[] => {
  return (rowData ?? currentPageTableData.value ?? []).filter(
    (d) => !d.childRowHeader && !d.childRow,
  );
};

const toggleRowExpand = async (
  data: T,
  indexOfData: number,
  isExpanding?: boolean,
): Promise<void> => {
  if (isRowExpanded(data[props.dataKey]) === isExpanding) return;
  const isExpandingRow = isExpanding ?? !isRowExpanded(data[props.dataKey]);

  if (!loadingRows.value) {
    if (!isExpandingRow) {
      currentPageTableData.value.splice(
        indexOfData + 1,
        expandedRows.value[data[props.dataKey]],
      );
      delete expandedRows.value[data[props.dataKey]];
    } else {
      expandedRows.value[data[props.dataKey]] = 1;

      let { children } = data;

      if (props.childTableProps?.fetchFunction && data.hasChildren) {
        try {
          // Add loading animation row
          currentPageTableData.value.splice(indexOfData + 1, 0, {
            childRow: true,
            loadingRow: true,
          } as TableRow);
          loadingRows.value = true;
          const fetchChildren =
            await props.childTableProps?.fetchFunction(data);
          children = fetchChildren?.data;
        } catch (error) {
          console.error('🚀 ~ toggleRowExpand ~ error:', error);
        } finally {
          // Remove loading animation row
          currentPageTableData.value.splice(indexOfData + 1, 1);
          loadingRows.value = false;
        }
      }

      if (indexOfData >= 0 && children?.length) {
        const childrenRows = children.flatMap((child) => {
          const rowHeader = {
            childRowHeader: true,
            header: child.groupHeader,
          } as TableRow;

          return [
            rowHeader,
            ...(child.groupItems ?? []).map(
              (d) => ({ childRow: true, ...d }) as TableRow,
            ),
          ];
        });

        expandedRows.value[data[props.dataKey]] = childrenRows.length;
        currentPageTableData.value.splice(
          indexOfData + 1,
          0,
          ...(childrenRows as T[]),
        );
      } else {
        currentPageTableData.value.splice(indexOfData + 1, 0, {
          childRow: true,
          noDataRow: true,
        } as TableRow);
      }
    }
  }
};

const toggleAllDataSelection = (e: boolean): void => {
  if (props.includeCheckDisabledRows) {
    checkboxSelection.value = e
      ? (filterParentRowData() as T[])
      : (filterParentRowData().filter((d) =>
          isRowDisabled(d[props.dataKey]),
        ) as T[]);
  } else {
    checkboxSelection.value = e
      ? (filterDisabledRows(filterParentRowData()) as T[])
      : [];
  }
};

const toggleRowSelection = (event: Event, data: T, index: number): void => {
  if (
    !data.childRow &&
    !data.childRowHeader &&
    !isRowDisabled(data[props.dataKey]) &&
    props.selectionType !== 'none'
  ) {
    const selected = isRowSelected(data[props.dataKey]);

    emit('rowSelect', { originalEvent: event, data, index });

    if (props.selectionType === 'checkbox') {
      if (selected)
        checkboxSelection.value = checkboxSelection.value.filter(
          (d) => d[props.dataKey] != data[props.dataKey],
        );
      else checkboxSelection.value = [...checkboxSelection.value, data];
    } else {
      singleSelection.value = selected ? undefined : data;
    }
  }
};

const toggleExpandAll = async (): Promise<void> => {
  const isExpanding = !isExpandedAll.value;
  if (!loadingRows.value) {
    let index = 0;
    for (const data of currentPageTableData.value) {
      if (data.children?.length || data.hasChildren) {
        await toggleRowExpand(data, index, isExpanding);
      }
      index++;
    }
  }
};

const toggleOptions = async (event: MouseEvent, data: T): Promise<void> => {
  const eventTarget = event.target as HTMLElement;
  const button =
    eventTarget.tagName.toLowerCase() === 'button'
      ? eventTarget
      : eventTarget.parentElement;

  emit('toggleOption', data);

  const toggleEvent: Event = {
    ...event,
    currentTarget: event.currentTarget ?? event.target,
  };

  removeClassActive();
  await nextTick(() => {
    if (button?.classList.contains('option-button-active')) {
      removeClassActive();
    } else {
      optionMenu.value?.toggle(toggleEvent);
      removeClassActive();
      button?.classList.add('option-button-active');
    }
  });
};

const bodySelectedClass = (
  col: TreeTableColumns<T>,
  item: T,
  dataKey: string,
): Record<string, boolean> => {
  const bodyClass: string =
    (typeof col.bodySelectedClass === 'function'
      ? col.bodySelectedClass(item)
      : col.bodySelectedClass) ?? '';
  return {
    [bodyClass]: isRowSelected(item[dataKey]),
  };
};

const onColumnTogglePresetChange = async (
  e: boolean,
  item: T,
  col: TreeTableColumns<T>,
): Promise<void> => {
  if (!col.preset) return;

  const revertFunction = (): void =>
    setNestedPropertyValue(
      item,
      col.field,
      !getNestedPropertyValue(item, col.field),
    );

  if (col.preset.type === 'checkbox')
    col.preset.onCheck?.(e, item, revertFunction);
  else if (col.preset.type === 'toggle')
    col.preset.onToggle?.(e, item, revertFunction);

  if ('confirmDialogProps' in col.preset) {
    const { showWhen } =
      getConfirmDialogProps(
        col.preset as ColumnTogglePreset,
        item[col.field],
        item,
      ) ?? {};

    if (typeof showWhen === 'function') {
      showConfirmToggle.value[item[props.dataKey] + col.field] = await showWhen(
        item,
        e,
      );
    } else {
      showConfirmToggle.value[item[props.dataKey] + col.field] =
        !showWhen ||
        (showWhen === 'active' && e) ||
        (showWhen === 'inactive' && !e);
    }
  }
};

const draggable = (item: DragableRow): boolean => {
  return (
    item.draggable !== false &&
    !item.childRow &&
    !item.childRowHeader &&
    props.reorderable &&
    !sortOrder.value
  );
};

/**
 * Starts the reordering of rows. Fired when user drag an item.
 *
 * @param {DragEvent} event - The drag event object.
 * @param {DragableRow} item - The row item being dragged.
 * @param {number} index - The index of the row item being dragged.
 */
const startReorderRow = (
  event: DragEvent,
  item: DragableRow,
  index: number,
): void => {
  if (item.childRow || item.childRowHeader) return;
  expandedRows.value = {}; // Collapse the expanded rows on Row Reorder

  const { dataTransfer } = event;
  draggedRow.value = item;

  rowReorderEventPayload.value = { item, fromIndex: index };

  if (dataTransfer) {
    dataTransfer.dropEffect = 'move';
    dataTransfer.effectAllowed = 'move';
    dataTransfer.setData('id', item[props.dataKey]);
    dataTransfer.setData('index', index.toString());
  }
};

const onDragEnter = (e: DragEvent, row: DragableRow): void => {
  e.preventDefault(); // <-- Still essential

  if (draggable(row)) {
    const target = e.target as HTMLElement;
    const dropTargetTr =
      target.tagName === 'TR' ? target : target.closest('tr');

    dropTargetTr?.classList.add('bg-primary-50');
    dropTargetTr?.classList.add('dark:bg-grayscale-800');
    dropTargetTr?.nextElementSibling?.classList.remove('bg-primary-50');
    dropTargetTr?.nextElementSibling?.classList.remove('dark:bg-grayscale-800');
    dropTargetTr?.previousElementSibling?.classList.remove('bg-primary-50');
    dropTargetTr?.previousElementSibling?.classList.remove(
      'dark:bg-grayscale-800',
    );

    if (!dropTargetTr || !draggedRow.value) return; // Safety checks

    const draggedIndex = rowReorderData.value.findIndex(
      (item) => item[props.dataKey] == draggedRow.value?.[props.dataKey],
    );

    const dropTargetIndex = rowReorderData.value.findIndex(
      (item) => item[props.dataKey] == dropTargetTr.id,
    );

    if (draggedIndex !== -1 && dropTargetIndex !== -1) {
      if (rowReorderEventPayload.value)
        rowReorderEventPayload.value.toIndex = dropTargetIndex;

      // Swap the items in columnReorderData
      [
        rowReorderData.value[draggedIndex],
        rowReorderData.value[dropTargetIndex],
      ] = [
        rowReorderData.value[dropTargetIndex],
        rowReorderData.value[draggedIndex],
      ];

      reorderVisibleRow();
    }
  }
};

/**
 * Handles the reordering of columns. Fired when user release the pointer.
 */
const onRowReorder = (): void => {
  if (rowReorderEventPayload.value) {
    emit('rowReorder', rowReorderEventPayload.value);
  }

  draggedRow.value = undefined;
  dragging.value = false;
  rowReorderEventPayload.value = undefined;
};

const reorderVisibleRow = (): void => {
  currentPageTableData.value = [...rowReorderData.value];
};

/**
 * Removes the active class from all buttons that have it.
 *
 * @returns {void}
 */
const removeClassActive = (): void => {
  const previouslyClicked = document.querySelectorAll('.option-button-active');

  previouslyClicked?.forEach((el) => {
    el.classList.remove('option-button-active');
  });
};

/**
 * This function resets data table-specific query parameters (page, limit, sortBy, sortOrder) back to their default value.
 */
const resetSortQuery = (): void => {
  sortBy.value = undefined;
  sortOrder.value = undefined;
};

const resetPage = (): void => {
  tablePage.value = 1;
  paginatorKey.value++;
};

const resetPageLimit = (): void => {
  tableRows.value = props.rows ?? 10;
  tablePage.value = 1;
  paginatorKey.value++;
};

const resetDataTableQuery = (): void => {
  resetPageLimit();
  resetSortQuery();
};

/**
 * On Search or Filter:
 * - Resets the table state by increment the key.
 * - Resets data table queryparams to default.
 */
const handleFilter = async (): Promise<void> => {
  tableKey.value++;
  paginatorKey.value++;

  resetDataTableQuery();
  if (!props.lazy) applyStaticFilter();
  else await reFetch();
};

const applyStaticFilter = (): void => {
  let filtered = [...(originalTableData.value ?? [])];
  const staticFilterMeta = filterQueryParams.value as Record<
    string,
    { value: any; matchMode: FilterMatchMode }
  >;

  const filterMetaEntries = Object.entries(staticFilterMeta);

  try {
    filtered = filterMetaEntries.length
      ? filtered.filter((d) => {
          return filterMetaEntries.every(
            ([key, { value: filterValue, matchMode }]) => {
              const fieldValue = getNestedPropertyValue(d, key);

              switch (matchMode) {
                case 'IN':
                  // Handle filtering for nested array, e.g field `tags.text`, tags is array, and text is property of each item in tags array
                  if (Array.isArray(fieldValue) && Array.isArray(filterValue)) {
                    return fieldValue.some((v) => filterValue.includes(v));
                  }

                  return Array.isArray(filterValue)
                    ? filterValue.includes(fieldValue)
                    : false;

                case 'CONTAINS':
                  return fieldValue && filterValue
                    ? fieldValue.toString().toLowerCase().includes(filterValue)
                    : false;

                case 'EQUALS':
                  return fieldValue
                    ? fieldValue.toString() === filterValue.toString()
                    : false;

                case 'BETWEEN':
                  // Range number comparation
                  return Array.isArray(filterValue) && filterValue.length === 2
                    ? !isNaN(fieldValue) &&
                        (filterValue[0] == null ||
                          fieldValue >= filterValue[0]) &&
                        (filterValue[1] == null || fieldValue <= filterValue[1])
                    : false;

                case 'DATE_BETWEEN':
                  // Comparing epoch time
                  return Array.isArray(filterValue)
                    ? +new Date(fieldValue) >= filterValue[0] &&
                        +new Date(fieldValue) <= filterValue[1]
                    : false;

                default:
                  return false;
              }
            },
          );
        })
      : originalTableData.value;
  } catch (error) {
    console.error('🚀 ~ applyStaticFilter ~ error:', error);
  }

  applyStaticSearch(filtered);
};

const applyStaticSearch = (data: T[]): void => {
  try {
    currentPageTableData.value = searchQueryParam.value?.length
      ? data.filter((d) => {
          const stringData = JSON.stringify(d);

          return (
            new RegExp(searchQueryParam.value, 'i').test(stringData) ||
            searchQueryParam.value
              .split(/\s+/)
              .filter(Boolean)
              .every((word) => {
                const regex = new RegExp(word, 'i');
                return regex.test(stringData);
              })
          );
        })
      : data;
  } catch (error) {
    console.error('🚀 ~ applyStaticSearch ~ error:', error);
  }
};

const handlePageChange = async (event: PageState): Promise<void> => {
  tableRows.value = event.rows;
  tablePage.value = event.page + 1;

  if (props.lazy && props.fetchFunction) await reFetch();

  /**
   * Maintain the rows expanded on page change
   */
  Object.keys(expandedRows.value).forEach((key) => {
    const index = currentPageTableData.value.findIndex(
      (d) => d[props.dataKey] == key,
    );
    if (index >= 0)
      toggleRowExpand(currentPageTableData.value[index], index, true);
  });
};

const sortColumn = (field: string, e: Event): void => {
  if (field !== sortBy.value) sortOrder.value = undefined; // Resets the sort order when the column sorted changed

  sortBy.value = field;

  if (!sortOrder.value) {
    sortOrder.value = 1;
  } else if (sortOrder.value == -1) {
    sortOrder.value = undefined;
    sortBy.value = undefined;
  } else {
    sortOrder.value = -1;
  }

  emit('sort', {
    sortOrder: sortOrder.value,
    sortField: sortBy.value,
    originalEvent: e,
    rows: tableRows.value,
  });

  resetPage();
  if (props.lazy && props.fetchFunction) reFetch();
  else staticSort();
};

const staticSort = (): void => {
  const data = [...(currentPageTableData.value ?? [])];
  const resolvedFieldData = new Map();

  if (!sortOrder.value) {
    currentPageTableData.value = originalTableData.value ?? [];

    return;
  }

  for (const item of data) {
    resolvedFieldData.set(item, getNestedPropertyValue(item, sortBy.value));
  }

  const comparer = new Intl.Collator(undefined, { numeric: true }).compare;

  data.sort((data1, data2) => {
    const value1 = resolvedFieldData.get(data1);
    const value2 = resolvedFieldData.get(data2);

    return ObjectUtils.sort(value1, value2, sortOrder.value, comparer, -1);
  });

  currentPageTableData.value = data;
};

const reFetch = async (): Promise<void> => {
  try {
    loadingTable.value = true;
    const response = await props.fetchFunction?.(queryParams.value);

    const { data = [], totalRecords: total = 0 } = response?.data ?? {};
    currentPageTableData.value = data;
    rowReorderData.value = [...currentPageTableData.value];
    totalRecords.value = total;
  } catch (error) {
    console.error('🚀 ~ reFetch ~ error:', error);
  } finally {
    loadingTable.value = false;
    expandedRows.value = {};
  }
};

/**
 * Used in export excel and event listener from Bulk action select all data.
 */
const fetchAllData = async (): Promise<FetchListResponse<T>['data']> => {
  const params = cloneDeep(queryParams.value);
  delete params.page;
  delete params.limit;

  const res = await props.fetchFunction?.(params);
  if (res instanceof Error) throw res;
  return res?.data ?? { data: [], totalRecords: 0 };
};

/**
 * Handle select all data from bulk action.
 */
const selectAllData = async (event: TableEvent): Promise<void> => {
  if (props.tableName !== event.tableName) return;

  try {
    loadingTable.value = true;
    const { data, totalRecords: total = 0 } = props.lazy
      ? await fetchAllData()
      : { totalRecords: (props.data ?? []).length, data: props.data };

    const disabledRows = getDisabledRows(data);
    checkboxSelection.value = props.includeCheckDisabledRows
      ? (data ?? [])
      : filterDisabledRows(data, disabledRows);

    totalRecords.value = total;
  } finally {
    loadingTable.value = false;
  }
};

const headerCellPreset = (
  col?: TreeTableColumns<T>,
  useCustomColumn?: boolean,
): Record<string, unknown> => {
  return Preset?.headercell({
    props,
    context: {
      sortOrder: sortOrder.value,
      customColumn: useCustomColumn,
      sorted: sortBy.value && sortBy.value === col?.field,
      sortable: col?.sortable,
      isParentHeader: (col as TableParentHeader)?.rowspan === 1,
    },
  });
};

const handleUpdateTableEvent = (event: TableEvent): void => {
  if (!event.tableName || event.tableName === props.tableName) {
    nextTick(() => {
      columnKey.value++;
      reFetch();
    }); // Waits until computed queryparams ready
  }
};

const filterDisabledRows = (
  tableData: T[] | undefined,
  rowsIDs?: string[],
): T[] => {
  return (tableData ?? []).filter(
    (data) =>
      !(rowsIDs ?? currentPageDisabledRows.value).includes(data[props.dataKey]),
  );
};

const getDisabledRows = (tableData: Data[] | undefined = []): string[] => {
  if (props.disableAllRows) return tableData.map((data) => data[props.dataKey]);
  return tableData
    .filter((data) => data[props.disableKey])
    .map((data) => data[props.dataKey]);
};

const downloadExcel = async ({
  tableName,
  multiTableNames,
  fileName,
  additionalTexts,
}: Events['data-table:download']): Promise<void> => {
  if (
    tableName !== props.tableName &&
    !multiTableNames?.includes(props.tableName)
  )
    return;

  const excelColumns = visibleColumns.value.filter(
    (col) => col.visible !== false,
  );

  const includedColumns = excelColumns.filter((col) => !col.excluded);
  const headers = includedColumns.map(
    (col) => col.header ?? col.exportHeader ?? '-',
  );

  const formatFileName = (): string => {
    return fileName
      .trim() // Remove extra space
      .replace(/[^a-zA-Z0-9\s]/g, '-') // Replace special characters with dash
      .replace(/\s+/g, '-') // Replace spaces with dash
      .replace(/-+/g, '-') // Replace multiple dashes with a single dash
      .replace(/-+$/, ''); // Remove trailing dash
  };

  try {
    // To make sure, 1 time event didn't listened twice
    eventBus.off('data-table:download', downloadHandler);
    // To make sure, if property `multiTableNames` is exist in event, then its tableName should equal to DataTable's tableName to call the fetch function and the rest of logics
    if (tableName === props.tableName) {
      setLoading(true, locale.downloadingMessage);

      const { data } = props.fetchFunction
        ? await fetchAllData()
        : { data: props.data };

      const excelBody = normalizeTableData(data, includedColumns, locale);

      if (multiTableNames?.length) {
        eventBus.emit('button-download:multi-tables', {
          table: {
            headers,
            data: excelBody,
            tableName,
            tableTitle: props.tableTitle,
          },
        });

        return;
      }

      /**
       * There's a little change to call util `exportToExcel`, just put `{headers, data}` as the value of key "tables"
       */
      await exportToExcel({
        tables: [
          { headers, data: excelBody, tableName, tableTitle: props.tableTitle },
        ],
        fileName: formatFileName(),
        additionalTexts,
      });
    }
  } catch (error) {
    console.error('🚀 ~ downloadExcel error:', error);
    toast.removeAllGroups();
    toast.add({
      message:
        locale.excelToastErrorMessage?.replace('{fileName}', fileName) ??
        'Data gagal diunduh.',
      error,
      group: 'download',
    });
  } finally {
    setLoading(false);
    eventBus.on('data-table:download', downloadHandler); // Re-listen the event when export excel finished
  }
};

onMounted(async () => {
  if (props.fetchFunction) {
    await reFetch();
    listenUpdateTableEvent();
  }

  if (props.selectedData?.length) {
    checkboxSelection.value = props.selectedData;
  }

  attachEventListener();
});

onUnmounted(() => {
  removeEventListener();
});

watch(
  () => props.columns,
  () => {
    if (!props.customColumn) visibleColumns.value = props.columns;
    customColumnKey.value++;
  },
);

watch(
  () => props.data,
  (data) => {
    currentPageTableData.value = data ?? [];
    originalTableData.value = data ?? [];
    rowReorderData.value = data ?? [];
    eventBus.emit('data-table:updated', { tableName: props.tableName, data });
    totalRecords.value = data?.length;
  },
  {
    immediate: true,
  },
);

watch(totalRecords, (total) => {
  updateTotalRecordBulkAction(total);
});

watch(
  checkboxSelection,
  (newSelectedData: T[]) => {
    emit('update:selectedData', newSelectedData);

    eventBus.emit('data-table:update-selected-data', {
      tableName: props.tableName,
      data: newSelectedData,
    });
  },
  { deep: true },
);

const attachEventListener = (): void => {
  eventBus.on('data-table:apply-filter', applyFilterHandler);
  eventBus.on('search-table', searchTableHandler);
  eventBus.on('search-by-scan-table', searchByScanHandler);
  eventBus.on('data-table:clear-selected-data', clearSelectedDataHandler);
  eventBus.on('data-table:select-all-record', selectAllRecordHandler);
  eventBus.on('data-table:download', downloadHandler);
  eventBus.on('data-table:import-excel-to-json', populateDataTableFromExcel);
};

const removeEventListener = (): void => {
  eventBus.off('data-table:update', handleUpdateTableEvent);
  eventBus.off('data-table:apply-filter', applyFilterHandler);
  eventBus.off('search-table', searchTableHandler);
  eventBus.off('search-by-scan-table', searchByScanHandler);
  eventBus.off('data-table:clear-selected-data', clearSelectedDataHandler);
  eventBus.off('data-table:select-all-record', selectAllRecordHandler);
  eventBus.off('data-table:download', downloadHandler);
  eventBus.off('data-table:import-excel-to-json', populateDataTableFromExcel);
};

const applyFilterHandler = (e: Events['data-table:apply-filter']): void => {
  if (e.tableName === props.tableName) {
    filterQueryParams.value = e.filter;
    handleFilter();
  }
};

const searchTableHandler = (e: Events['search-table']): void => {
  if (e.tableName === props.tableName) {
    searchQueryParam.value = e.search;
    handleFilter();
  }
};

const searchByScanHandler = (e: Events['search-by-scan-table']): void => {
  if (e.tableName === props.tableName) {
    tagQueryParam.value = e.tag;
    handleFilter();
  }
};

const selectAllRecordHandler = (
  e: Events['data-table:select-all-record'],
): void => {
  if (e.tableName === props.tableName) {
    selectAllData(e);
  }
};

const clearSelectedDataHandler = (
  e: Events['data-table:clear-selected-data'],
): void => {
  if (e.tableName === props.tableName) {
    checkboxSelection.value = [];
  }
};

const downloadHandler = (e: Events['data-table:download']): void => {
  if (
    e.tableName === props.tableName ||
    e.multiTableNames?.includes(props.tableName)
  ) {
    downloadExcel(e);
  }
};

const preventCharInput = (e: KeyboardEvent): void => {
  if (
    !(
      e.code.includes('Digit') ||
      e.code.includes('Numpad') ||
      ['ArrowLeft', 'ArrowRight', 'Backspace', 'Delete'].includes(e.code)
    )
  ) {
    e.preventDefault();
  }
};

/**
 * Will be executed when the total record is changed on both lazy and static table
 *
 * @param total
 */
const updateTotalRecordBulkAction = (total?: number): void => {
  const disabledCount = props.includeCheckDisabledRows
    ? 0
    : ((currentPageDisabledRows.value.length || props.totalDisabledRows) ?? 0);

  const data = {
    total: (total ?? totalRecords.value ?? 0) - disabledCount,
    tableName: props.tableName,
  };

  eventBus.emit('data-table:update-total-record', data);
};

const listenUpdateTableEvent = (): void => {
  eventBus.on('data-table:update', handleUpdateTableEvent);
};

const populateDataTableFromExcel = (
  e: Events['data-table:import-excel-to-json'],
): void => {
  if (e.tableName === props.tableName) {
    currentPageTableData.value = e.data as T[];
    rowReorderData.value = [...currentPageTableData.value];
    totalRecords.value = e.data.length;
  }
};

const getConfirmDialogProps = (
  preset: ColumnTogglePreset,
  state: boolean,
  rowData: any,
): TogglePresetConfirmDialogProps => {
  return typeof preset?.confirmDialogProps == 'function'
    ? preset?.confirmDialogProps(state, rowData)
    : preset?.confirmDialogProps;
};
</script>

<template>
  <div v-bind="Preset?.root">
    <div v-bind="Preset?.tablewrapper">
      <Simplebar
        :auto-hide="false"
        v-bind="Preset?.scrollheightwrapper({ props })"
        class="custom-scrollbar [&_.simplebar-track]:bg-grayscale-100 dark:[&_.simplebar-track]:bg-grayscale-800"
      >
        <table
          :id="dataTableID"
          v-bind="Preset?.table"
          :key="tableKey"
          :ref="tableElement"
        >
          <thead v-bind="Preset.thead">
            <tr v-bind="Preset.headerrow">
              <th
                v-if="reorderable && !sortOrder"
                v-bind="Preset.headercellreorderable"
                :class="headerCellPreset().class"
                :rowspan="defaultHeaderRowSpan"
              />

              <th
                v-if="selectionType === 'checkbox'"
                v-bind="Preset.headercellcheckbox"
                :class="headerCellPreset().class"
                :rowspan="defaultHeaderRowSpan"
              >
                <Checkbox
                  v-bind="
                    Preset?.headercheckbox({ context: { isSelectedAll } })
                  "
                  :model-value="isSelectedAll"
                  @update:model-value="toggleAllDataSelection(!!$event)"
                  binary
                />
              </th>

              <th
                v-if="treeTable"
                v-bind="Preset.headertoggler"
                :class="headerCellPreset().class"
                :rowspan="defaultHeaderRowSpan"
              >
                <Button
                  @click="toggleExpandAll"
                  v-bind="
                    Preset.headertogglerbutton({ context: { isExpandedAll } })
                  "
                  icon="arrow-down"
                  text
                />
              </th>

              <HeaderCell
                :key="col.field"
                v-for="col in combinedHeaders.parent"
                v-bind="headerCellPreset(col)"
                :col="col"
                :default-header-row-span="defaultHeaderRowSpan"
                :preset="Preset"
                :sort-by="sortBy"
                :sort-order="sortOrder"
                @click="col.sortable ? sortColumn(col.field, $event) : null"
              />

              <th
                v-bind="headerCellPreset(undefined, props.customColumn)"
                :id="`column-visibility-toggle-${dataTableID}`"
                v-if="showCustomColumn"
                :rowspan="defaultHeaderRowSpan"
                @click="customColumn?.toggleMenu"
              >
                <Icon
                  v-if="props.customColumn"
                  v-bind="Preset.columnvisibilityicon"
                  icon="ellipsis-h"
                  tooltip-pos="left"
                />
              </th>
            </tr>

            <tr v-if="combinedHeaders.child.length" v-bind="Preset.headerrow">
              <HeaderCell
                :key="col.field"
                v-for="col in combinedHeaders.child"
                v-bind="headerCellPreset(col)"
                :col="col"
                :default-header-row-span="defaultHeaderRowSpan"
                :preset="Preset"
                :sort-by="sortBy"
                :sort-order="sortOrder"
                @click="col.sortable ? sortColumn(col.field, $event) : null"
              />
            </tr>
          </thead>

          <tbody v-bind="Preset?.tbody">
            <template v-if="tableIsLoading">
              <tr>
                <td :colspan="columnsCount" v-bind="Preset?.bodycell">
                  <!-- A duplicate is created to expand the height of the table -->
                  <template
                    :key="i"
                    v-for="i in Preset.invisiblelottiewrapper ? 2 : 1"
                  >
                    <div
                      v-bind="
                        i === 1
                          ? Preset.loadingtablewrapper
                          : Preset.invisiblelottiewrapper
                      "
                    >
                      <Animation
                        animation="loading-table"
                        v-bind="Preset.loadingtablelottie"
                      />
                    </div>
                  </template>
                </td>
              </tr>
            </template>

            <template v-else-if="currentPageHasNoData">
              <tr>
                <td :colspan="columnsCount" v-bind="Preset?.bodycell">
                  <!-- A duplicate is created to expand the height of the table -->
                  <template
                    :key="i"
                    v-for="i in Preset.invisiblelottiewrapper ? 2 : 1"
                  >
                    <div
                      v-bind="
                        i === 1
                          ? Preset.nodatalottiewrapper
                          : Preset.invisiblelottiewrapper
                      "
                    >
                      <Animation
                        animation="no-data"
                        v-bind="Preset.nodatalottie"
                      />

                      <span
                        v-if="locale.emptyTableMessage"
                        v-bind="Preset.nodatamessage"
                      >
                        {{ locale.emptyTableMessage }}
                      </span>
                    </div>
                  </template>
                </td>
              </tr>
            </template>

            <template v-else>
              <template
                :key="`${index}-${item[dataKey]}`"
                v-for="(item, index) in renderedTableData"
              >
                <tr
                  :id="item[props.dataKey]"
                  :class="[
                    typeof props.rowClass === 'function'
                      ? props.rowClass(item)
                      : props.rowClass,
                  ]"
                  :draggable="draggable(item)"
                  v-bind="
                    Preset?.bodyrow({
                      context: {
                        highlighted: item[highlightKey ?? ''],
                        selected: isRowSelected(item[dataKey]),
                        disabled: isRowDisabled(item[dataKey]),
                        draggable: draggable(item),
                        dragging,
                      },
                      props: config,
                    })
                  "
                  @click="toggleRowSelection($event, item, index)"
                  @dblclick="
                    treeTable && (item.children?.length || item.hasChildren)
                      ? toggleRowExpand(item, index)
                      : $emit('doubleClick', item)
                  "
                  @drag="dragging = true"
                  @dragenter.prevent="onDragEnter($event, item)"
                  @dragleave.prevent=""
                  @dragover.prevent=""
                  @dragstart="startReorderRow($event, item, index)"
                  @drop="onRowReorder"
                >
                  <td
                    v-if="reorderable && !sortOrder"
                    v-bind="Preset?.bodycell"
                  >
                    <Icon
                      v-if="!item.childRow && !item.childRowHeader"
                      class="draggableicon"
                      icon="dragable-menu"
                    />
                  </td>

                  <td
                    v-if="selectionType === 'checkbox'"
                    v-bind="Preset?.bodycell"
                    @click.stop=""
                  >
                    <Checkbox
                      v-if="!item.childRow && !item.childRowHeader"
                      v-bind="Preset?.rowcheckbox"
                      v-model="checkboxSelection"
                      :binary="false"
                      :disabled="isRowDisabled(item[dataKey])"
                      :value="item"
                    />
                  </td>

                  <td
                    v-if="treeTable"
                    v-bind="Preset?.bodycell"
                    @click="
                      (e) => {
                        (item.childRow || item.childRowHeader) &&
                          e.stopPropagation();
                      }
                    "
                  >
                    <Button
                      v-if="item.children?.length || item.hasChildren"
                      @click.stop="toggleRowExpand(item, index)"
                      v-bind="
                        Preset.rowtogglerbutton({
                          context: {
                            isRowExpanded: isRowExpanded(item[dataKey]),
                          },
                        })
                      "
                      icon="arrow-down"
                      text
                    />
                  </td>

                  <template
                    v-if="
                      (() =>
                        item.childRowHeader &&
                        props.childTableProps?.useColumnsHeader)()
                    "
                  >
                    <td
                      :key="col.header"
                      v-for="col in visibleChildTableColumns"
                      :class="[Preset?.bodycell.class, 'font-semibold text-xs']"
                      :colspan="col.colspan ?? col.parentColumnsFields?.length"
                      @click.stop=""
                    >
                      {{ col.header }}
                    </td>

                    <td
                      v-if="showCustomColumn"
                      :class="Preset.bodycell.class"
                      @click.stop=""
                    />
                  </template>

                  <td
                    v-else-if="item.childRowHeader"
                    v-bind="Preset.childrowheader"
                    :class="Preset?.bodycell.class"
                    :colspan="props.columns.length + 1"
                    @click.stop=""
                  >
                    {{ item.header }}
                  </td>

                  <td
                    v-else-if="item.loadingRow"
                    :class="Preset?.bodycell.class"
                    :colspan="props.columns.length + 1"
                    @click.stop=""
                  >
                    <div v-bind="Preset.loadingtablewrapper">
                      <Animation
                        animation="loading-table"
                        v-bind="Preset.loadingtablelottie"
                      />
                    </div>
                  </td>

                  <td
                    v-else-if="item.noDataRow"
                    :class="Preset?.bodycell.class"
                    :colspan="props.columns.length + 1"
                    @click.stop=""
                  >
                    <div v-bind="Preset.nodatalottiewrapper">
                      <Animation
                        animation="no-data"
                        v-bind="Preset.nodatalottie"
                      />

                      <span
                        v-if="locale.emptyTableMessage"
                        v-bind="Preset.nodatamessage"
                      >
                        {{ locale.emptyTableMessage }}
                      </span>
                    </div>
                  </td>

                  <template v-else>
                    <td
                      :key="col.field"
                      v-for="col in item.childRow
                        ? visibleChildTableColumns
                        : visibleColumns"
                      :class="[
                        {
                          '!py-0': col.editable,
                          '!px-0': col.preset?.type === 'multirow',
                        },
                        typeof col.bodyClass === 'function'
                          ? col.bodyClass(item)
                          : col.bodyClass,
                        bodySelectedClass(col, item, dataKey),
                        col.class,
                      ]"
                      :colspan="col.colspan ?? col.parentColumnsFields?.length"
                      v-bind="Preset?.bodycell"
                    >
                      <template
                        v-if="
                          col.preset?.type === 'toggle' ||
                          col.preset?.type === 'checkbox'
                        "
                      >
                        <ToggleSwitch
                          v-if="col.preset?.type === 'toggle'"
                          :disabled="
                            isRowDisabled(item[dataKey]) ||
                            (typeof col.preset?.disabled === 'function'
                              ? col.preset?.disabled(item)
                              : col.preset?.disabled)
                          "
                          :input-id="item[dataKey]"
                          :model-value="getNestedPropertyValue(item, col.field)"
                          @click.stop=""
                          @update:model-value="
                            (e) => {
                              setNestedPropertyValue(item, col.field, e);
                              onColumnTogglePresetChange(e, item, col);
                            }
                          "
                        />

                        <Checkbox
                          v-if="col.preset?.type === 'checkbox'"
                          :disabled="
                            isRowDisabled(item[dataKey]) ||
                            (typeof col.preset?.disabled === 'function'
                              ? col.preset?.disabled(item)
                              : col.preset?.disabled)
                          "
                          :input-id="item[dataKey]"
                          :model-value="getNestedPropertyValue(item, col.field)"
                          @click.stop=""
                          @update:model-value="
                            (e) => {
                              setNestedPropertyValue(item, col.field, !!e);
                              onColumnTogglePresetChange(!!e, item, col);
                            }
                          "
                          binary
                        />

                        <DialogConfirm
                          v-if="col.preset?.confirmDialogProps"
                          :list="[item]"
                          :visible="
                            !!showConfirmToggle[item[dataKey] + col.field]
                          "
                          @close="
                            () => {
                              const revert = () =>
                                setNestedPropertyValue(
                                  item,
                                  col.field,
                                  !getNestedPropertyValue(item, col.field),
                                );

                              if (
                                getConfirmDialogProps(
                                  col.preset as ColumnTogglePreset,
                                  item[col.field],
                                  item,
                                )?.actionable == false &&
                                'onConfirm' in col.preset
                              ) {
                                col.preset.onConfirm?.(
                                  item[col.field],
                                  item,
                                  revert,
                                );
                              } else revert();
                            }
                          "
                          @confirm="
                            col.preset?.onConfirm?.(item[col.field], item, () =>
                              setNestedPropertyValue(
                                item,
                                col.field,
                                !getNestedPropertyValue(item, col.field),
                              ),
                            )
                          "
                          @update:visible="
                            showConfirmToggle[item[dataKey] + col.field] =
                              $event
                          "
                          actionable
                          v-bind="
                            getConfirmDialogProps(
                              col.preset as ColumnTogglePreset,
                              item[col.field],
                              item,
                            )
                          "
                        />
                      </template>

                      <template v-else-if="col.preset?.type === 'multirow'">
                        <MultiRow
                          :attributes="
                            col.preset?.fieldAttributes?.(item) ?? []
                          "
                        />
                      </template>

                      <template v-else-if="col.editable">
                        <span
                          v-bind="Preset.celleditableelement"
                          :contenteditable="col.editable"
                          @blur="
                            (e: Event) => {
                              $emit('cellEdited', {
                                item,
                                field: col.field,
                                index,
                                value: (e.target as HTMLElement).innerText,
                              });
                            }
                          "
                          @keypress="
                            if (col.numberInputOnly) preventCharInput($event);
                          "
                        >
                          {{ getNestedPropertyValue(item, col.field) || '-' }}
                        </span>
                      </template>

                      <template
                        v-else-if="
                          col.bodyComponent ||
                          col.bodyClass ||
                          col.bodyTemplate ||
                          col.dateFormatOptions
                        "
                      >
                        <component
                          :is="col.bodyComponent!(item, index).component"
                          v-if="col.bodyComponent"
                          v-model="col.bodyComponent!(item, index).model"
                          :disabled="col.bodyComponent!(item, index).disabled"
                          v-bind="col.bodyComponent!(item, index).props"
                          v-on="
                            col.bodyComponent!(item, index).events
                              ? col.bodyComponent!(item, index).events
                              : {}
                          "
                          @change="
                            col.bodyComponent!(item, index).onChange?.(item)
                          "
                          @click="
                            (e: MouseEvent) => {
                              if (
                                col.bodyComponent!(item, index).stop !== false
                              ) {
                                e.stopPropagation();
                              }
                            }
                          "
                          @update:model-value="
                            col.bodyComponent!(item, index).onChange?.(item)
                          "
                        />

                        <span v-else>
                          <template v-if="col.bodyTemplate">
                            {{ col.bodyTemplate?.(item, index) || '-' }}
                          </template>

                          <template v-else-if="col.dateFormatOptions">
                            {{
                              formatDate(
                                new Date(
                                  getNestedPropertyValue(
                                    item,
                                    col.field,
                                  ) as string,
                                ),
                                col.dateFormatOptions,
                              )
                            }}
                          </template>

                          <template v-else>
                            {{ getNestedPropertyValue(item, col.field) || '-' }}
                          </template>
                        </span>
                      </template>

                      <template v-else>
                        {{ getNestedPropertyValue(item, col.field) ?? '-' }}
                      </template>
                    </td>

                    <td
                      v-if="showCustomColumn"
                      v-bind="
                        Preset?.rowsingleactioncell({
                          props,
                          context: {
                            selected: isRowSelected(item[dataKey]),
                            highlighted: item[highlightKey ?? ''],
                          },
                        })
                      "
                      :class="Preset.bodycell.class"
                    >
                      <div
                        v-if="
                          (!item.childRow && useOption) ||
                          (item.childRow && props.childTableProps?.useOption)
                        "
                        v-bind="Preset.singleactionwrapper"
                      >
                        <Button
                          :id="'button-action-' + item[props.dataKey]"
                          v-bind="Preset.singleactionbutton({ props })"
                          :disabled="disableAllRows"
                          @click.stop="toggleOptions($event, item)"
                          icon="ellipsis-h"
                          outlined
                          size="small"
                          tooltip-pos="left"
                          type="button"
                        />
                      </div>
                    </td>
                  </template>
                </tr>

                <slot :item="item" name="row-subcontent" />
              </template>
            </template>
          </tbody>
        </table>
      </Simplebar>
    </div>

    <Paginator
      :key="paginatorKey"
      v-if="usePaginator"
      v-show="!tableIsLoading"
      v-model:rows="tableRows"
      v-bind="Preset.paginator({ context: { totalRecords } })"
      :current-page-report-template="
        totalRecords
          ? locale.paginationReportFound
          : locale.paginationReportNotFound
      "
      :rows-per-page-options="rowsPerPageOptions"
      :total-records="totalRecords"
      @page="handlePageChange"
    >
      <template #firstpagelinkicon>
        <Icon icon="round-keyboard-double-arrow-left" />
      </template>

      <template #lastpagelinkicon>
        <Icon icon="round-keyboard-double-arrow-right" />
      </template>

      <template #prevpagelinkicon>
        <Icon icon="round-keyboard-arrow-left" />
      </template>

      <template #nextpagelinkicon>
        <Icon icon="round-keyboard-arrow-right" />
      </template>
    </Paginator>

    <Menu
      id="single-action-menu"
      ref="optionMenu"
      :model="options"
      @focus="adjustMenuPositionHelper"
      append-to="body"
      auto-z-index
      popup
    />

    <CustomColumn
      :key="customColumnKey"
      ref="customColumn"
      v-if="props.customColumn"
      v-model:visible-columns="visibleColumns"
      :default-columns="columns.filter((col) => col.visible !== false)"
      :table-id="dataTableID"
    />

    <Toast group="download" />
  </div>
</template>

<style scoped>
:deep(.custom-scrollbar) {
  .simplebar-scrollbar {
    pointer-events: auto !important; /* Prevent the scrollbar from clicking through */
  }

  .simplebar-track {
    pointer-events: auto;
  }

  .simplebar-scrollbar::before {
    background: rgb(192, 192, 192); /* Change thumb color */
    opacity: 1;
    left: 4px;
    right: 4px;
  }

  .simplebar-scrollbar:hover::before,
  .simplebar-scrollbar:active::before {
    background: rgb(140, 140, 140);
  }

  .simplebar-content-wrapper,
  .simplebar-wrapper {
    padding-bottom: 12px !important;
    border-radius: 8px;
  }

  .simplebar-track.simplebar-vertical {
    width: 0px; /* Hide vertical scrollbar */
  }

  .simplebar-track.simplebar-horizontal {
    height: 10px;
  }
}
</style>
